import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  createCoupon,
  deleteCoupon,
  listCoupons,
  updateCoupon,
  type Coupon,
  type CouponInput,
} from '../lib/api';
import { useProductSearch } from '../lib/use-product-search';
import { useProductsByIds } from '../lib/use-products-by-ids';

/**
 * Coupons admin. Each coupon is a percent or fixed-amount discount that applies
 * per-line to an admin-selected set of products (only those lines discount).
 * One-time-use or reusable-within-a-time-window.
 */
export function CouponsPage() {
  const qc = useQueryClient();
  const couponsQuery = useQuery({ queryKey: ['admin-coupons'], queryFn: listCoupons });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: deleteCoupon,
    meta: { successMessage: 'Coupon deleted.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-coupons'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Coupons</h1>
        <Button onClick={() => setCreating(true)}>+ New coupon</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Coupons discount only the selected products, per line (other items in the cart are
        unaffected). They apply to cash orders only and never reduce PV.
      </p>

      {creating && (
        <CouponForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin-coupons'] });
            setCreating(false);
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All coupons</CardTitle>
        </CardHeader>
        <CardContent>
          {couponsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (couponsQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No coupons yet.</p>
          ) : (
            <ul className="divide-y">
              {couponsQuery.data!.map((c) =>
                editing === c.id ? (
                  <li key={c.id} className="py-3">
                    <CouponForm
                      coupon={c}
                      onClose={() => setEditing(null)}
                      onSaved={() => {
                        qc.invalidateQueries({ queryKey: ['admin-coupons'] });
                        setEditing(null);
                      }}
                    />
                  </li>
                ) : (
                  <li key={c.id} className="flex items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{c.code}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {c.discountType === 'percent'
                            ? `${Number(c.discountValue)}% off`
                            : `${Number(c.discountValue).toFixed(2)} off / unit`}
                        </span>
                        {!c.active && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.oneTime
                          ? 'One-time use'
                          : c.maxUses != null
                            ? `Up to ${c.maxUses} uses`
                            : 'Unlimited uses'}
                        {' · '}
                        {c.usedCount} used
                        {c.perUserLimit != null && ` · ${c.perUserLimit}/member`}
                        {' · '}
                        {c.productIds.length} product(s)
                        {(c.validFrom || c.validUntil) &&
                          ` · ${c.validFrom ? new Date(c.validFrom).toLocaleDateString() : '…'}–${c.validUntil ? new Date(c.validUntil).toLocaleDateString() : '…'}`}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditing(c.id)}>
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete coupon "${c.code}"?`)) remove.mutate(c.id);
                      }}
                    >
                      Delete
                    </Button>
                  </li>
                ),
              )}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CouponForm({
  coupon,
  onClose,
  onSaved,
}: {
  coupon?: Coupon;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(coupon?.code ?? '');
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>(
    coupon?.discountType ?? 'percent',
  );
  const [discountValue, setDiscountValue] = useState(coupon?.discountValue ?? '');
  const [oneTime, setOneTime] = useState(coupon?.oneTime ?? false);
  const [maxUses, setMaxUses] = useState(coupon?.maxUses != null ? String(coupon.maxUses) : '');
  const [perUserLimit, setPerUserLimit] = useState(
    coupon?.perUserLimit != null ? String(coupon.perUserLimit) : '',
  );
  const [validFrom, setValidFrom] = useState(toDateInput(coupon?.validFrom));
  const [validUntil, setValidUntil] = useState(toDateInput(coupon?.validUntil));
  const [active, setActive] = useState(coupon?.active ?? true);
  const [productIds, setProductIds] = useState<string[]>(coupon?.productIds ?? []);
  const [search, setSearch] = useState('');

  // Candidates come from the SERVER (FTS + trigram, top 20 by relevance). The old
  // shape — one `pageSize: 200` catalog page filtered in the browser — made every
  // product past that page unreachable while looking like it worked.
  const { results, isLoading: searching } = useProductSearch({
    term: search,
    filter: { activeOnly: true },
  });
  // The eligible-products list used to BE the catalog, so a product selected but
  // absent from that page had no checkbox to untick — invisible and unremovable
  // while still saved. The selection is now rendered on its own, resolved by id
  // (archived included), and the list below is purely "search to add".
  const { products: selectedProducts } = useProductsByIds(productIds);

  const save = useMutation({
    mutationFn: () => {
      const payload: CouponInput = {
        code: code.trim(),
        discountType,
        discountValue: discountValue.trim(),
        oneTime,
        maxUses: oneTime ? null : maxUses.trim() === '' ? null : Number(maxUses),
        perUserLimit: perUserLimit.trim() === '' ? null : Number(perUserLimit),
        validFrom: validFrom ? new Date(validFrom).toISOString() : null,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        active,
        productIds,
      };
      return coupon ? updateCoupon(coupon.id, payload) : createCoupon(payload);
    },
    meta: { successMessage: 'Coupon saved.' },
    onSuccess: onSaved,
  });

  function toggleProduct(id: string) {
    setProductIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const err = (save.error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;
  const canSave = code.trim() && discountValue.trim() && productIds.length > 0;

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3">
      <h3 className="text-base font-semibold">{coupon ? 'Edit coupon' : 'New coupon'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={!!coupon}
            placeholder="SAVE10"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select
              value={discountType}
              onValueChange={(v) => setDiscountType(v as 'percent' | 'amount')}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">% off</SelectItem>
                <SelectItem value="amount">Amount off / unit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {discountType === 'percent' ? 'Percent (0–100)' : 'Amount'}
            </label>
            <Input
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder="10"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Max uses (blank = unlimited)</label>
          <Input
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            disabled={oneTime}
            type="number"
            min={1}
            placeholder={oneTime ? '1 (one-time)' : ''}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            Per-member limit (blank = unlimited)
          </label>
          <Input
            value={perUserLimit}
            onChange={(e) => setPerUserLimit(e.target.value)}
            type="number"
            min={1}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Valid from (optional)</label>
          <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Valid until (optional)</label>
          <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={oneTime} onCheckedChange={(v) => setOneTime(v === true)} />
          One-time use (single global use)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
          Active
        </label>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">
          Eligible products ({productIds.length} selected) — only these lines get the discount
        </label>
        {selectedProducts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 rounded border bg-muted/30 p-2">
            {selectedProducts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => toggleProduct(p.id)}
                title="Remove"
                className="flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs hover:bg-muted"
              >
                <span className="truncate">{p.name}</span>
                <span className="font-mono text-muted-foreground">{p.sku}</span>
                {!p.active && <span className="text-muted-foreground">· archived</span>}
                <span aria-hidden>×</span>
              </button>
            ))}
          </div>
        )}
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          className="max-w-xs"
        />
        <div className="max-h-56 overflow-y-auto rounded border bg-background p-2 space-y-1">
          {results.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm py-0.5">
              <Checkbox
                checked={productIds.includes(p.id)}
                onCheckedChange={() => toggleProduct(p.id)}
              />
              <span className="truncate">{p.name}</span>
              <span className="text-xs text-muted-foreground font-mono">{p.sku}</span>
            </label>
          ))}
          {results.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {searching ? 'Searching…' : 'No products match.'}
            </p>
          )}
        </div>
      </div>

      {err && <p className="text-sm text-destructive">{err}</p>}
      <div className="flex gap-2 pt-1">
        <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : coupon ? 'Save' : 'Create'}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** ISO timestamp → yyyy-mm-dd for a <input type="date">. */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}
